import React, { memo, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { EventSubCategory } from '../../../../../redux/incident/EventCategory/EventCategory.model';
import { cardHeaderSx, titleSx } from './styles';
import SubCategoryRow from './SubCategoryRow';

interface CategoryCardProps {
  categoryId: string;
  name: string;
  catState: 'some' | 'all' | 'none';
  subCategories: EventSubCategory[];
  selectedSubCategoryDic: Record<string, 'some' | 'all' | 'none'>;
  toggleCategory: (categoryId: string) => string[];
  toggleSubCategory: (categoryId: string, subCategoryId: string) => string[];
}

const CategoryCard = memo(function CategoryCard({
  categoryId,
  name,
  catState,
  subCategories,
  selectedSubCategoryDic,
  toggleCategory,
  toggleSubCategory,
}: CategoryCardProps) {
  const handleCategoryChange = useCallback(
    () => toggleCategory(categoryId),
    [toggleCategory, categoryId]
  );

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          sx={cardHeaderSx}
          title={
            <FormControlLabel
              control={
                <Checkbox
                  checked={catState === 'all'}
                  indeterminate={catState === 'some'}
                  onChange={handleCategoryChange}
                  size="small"
                />
              }
              label={
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{
                    fontWeight: 'bold',
                    noWrap: true,
                    color: 'primary.contrastText',
                  }}
                  sx={titleSx}
                />
              }
              sx={{ m: 0, color: 'primary.contrastText' }}
            />
          }
        />
        {subCategories.length > 0 && (
          <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
            <FormGroup>
              {subCategories.map((sub) => (
                <SubCategoryRow
                  key={sub.id}
                  categoryId={categoryId}
                  sub={sub}
                  state={
                    selectedSubCategoryDic[`${categoryId}.${sub.id}`] ?? 'none'
                  }
                  toggleSubCategory={toggleSubCategory}
                />
              ))}
            </FormGroup>
          </CardContent>
        )}
      </Card>
    </Grid>
  );
});

export default CategoryCard;
